import React, { useContext } from "react";
import { DefaultButton, FontIcon, Spinner, SpinnerSize } from "@fluentui/react";
import { ThemeContext } from "@fluentui/react-theme-provider";
import { ThemeProvider } from "styled-components";
import DocumentTitle from "react-document-title";
import CountryPrefixes from "@/utils/CountryPrefixes";
import Regions from "@/utils/Regions";
import CompanyOnboardingStepper, { CompanyOnboardingStep } from "@/screen/Companies/components/CompanyOnboardingStepper";
import CompanySectionCard from "@/screen/Companies/components/CompanySectionCard";
import CompanyAddressFields from "@/screen/Companies/CompanyDetails/CompanyAddressFields";
import CompanyDocumentsSection from "@/screen/Companies/CompanyDetails/CompanyDocumentsSection";
import CompanyRepresentativesSection from "@/screen/Companies/CompanyDetails/CompanyRepresentativesSection";
import CompanyDetailsSectionNav from "@/screen/Companies/CompanyDetails/CompanyDetailsSectionNav";
import { CompanyDetailsSections } from "@/screen/Companies/CompanyDetails/companyDetailsSections";
import { useCompanyDetailsController } from "@/screen/Companies/CompanyDetails/useCompanyDetailsController";
import DropdownSelect from "react-dropdown-select";
import { SearchableSelectWrapper } from "@/screen/Identities/IdentityFiscalInputs/Styles/IdentityFiscalInputsStyles";
import { SectorOptions } from "@/screen/Companies/CompanyDetails/companyDetailsForm";
import type { CatalogOption, CnaeOption, CompanyDetailsTextField } from "@/screen/Companies/CompanyDetails/companyDetailsForm";
import { CompanyOnboardingStrings } from "@/screen/Companies/companyOnboarding.strings";
import { Cont, MainCont, BackLink, Header, ErrorNotice, SubmitNotice, InfoBanner } from "@/screen/Companies/Styles/CompanyOnboardingStyles";
import { Field, FieldError, FieldGrid, Input, Select, SubsectionTitle, TwoFieldRow } from "@/screen/Companies/Styles/CompanyFormFieldStyles";
import { CheckRow, DetailsFooter, DetailsLayout, FormFieldset, Pill, YesNoRow } from "@/screen/Companies/Styles/CompanyDetailsStyles";

const CountryOptions: CatalogOption[] = CountryPrefixes
    .filter((country) => country.iso2)
    .map((country) => ({ value: country.iso2, text: country.name }));

const PhonePrefixOptions: CatalogOption[] = CountryPrefixes
    .filter((country) => country.phone_code)
    .map((country) => ({ value: country.phone_code, text: `${country.name} (${country.phone_code})` }));

const ProvinceOptions: CatalogOption[] = Regions.map((region) => ({ value: region.text, text: region.text }));

// Ancla de cada sección para el índice lateral (1 = información básica ... 6 = representantes)
const sectionId = (sectionNumber: number): string => CompanyDetailsSections[sectionNumber - 1].id;

const renderOptions = (options: CatalogOption[]): React.ReactNode =>
(
    <React.Fragment>
        <option value="">-- Seleccionar --</option>
        {/* Hay prefijos compartidos por varios países (p. ej. +1): la clave incluye el índice */}
        {options.map((option, index) => <option key={`${option.value}-${index}`} value={option.value}>{option.text}</option>)}
    </React.Fragment>
);

// Paso 3 del alta de empresa: datos, registro, domicilios, contacto, documentación y representantes. Todo queda como
// borrador en IronIA; se envía a Omnia al terminar el KYC de la empresa (paso 4).
const CompanyDetailsScreen: React.FC = () =>
{
    const themeContext = useContext(ThemeContext);
    const controller = useCompanyDetailsController();
    const {phase, formData} = controller;

    const required = (label: string): React.ReactNode => <span>{label} <b>*</b></span>;
    const missing = (field: CompanyDetailsTextField): React.ReactNode =>
        controller.isMissing(field) ? <FieldError>Este dato es obligatorio o no es válido.</FieldError> : null;
    const textInput = (name: CompanyDetailsTextField, maxLength: number, type: string = "text", placeholder?: string): React.ReactNode =>
        <Input type={type} name={name} value={formData?.[name] ?? ""} onChange={controller.handleChange} maxLength={maxLength} placeholder={placeholder} autoComplete="off" />;

    return(
        <DocumentTitle title={`IronIA - ${CompanyOnboardingStrings.detailsTitle}`}>
            <Cont theme={themeContext}>
                <ThemeProvider theme={themeContext}>
                    <MainCont>
                        <BackLink type="button" onClick={controller.goToIdentities}>
                            <FontIcon iconName="ChevronLeft" />
                            {CompanyOnboardingStrings.backToIdentities}
                        </BackLink>
                        <Header>
                            <h1>{CompanyOnboardingStrings.detailsTitle}</h1>
                            <h2>{CompanyOnboardingStrings.detailsSubtitle}</h2>
                        </Header>
                        <CompanyOnboardingStepper currentStep={CompanyOnboardingStep.CompanyData} />

                        {phase === "loading" && <Spinner size={SpinnerSize.large} label={CompanyOnboardingStrings.detailsLoading} />}
                        {phase === "error" && <ErrorNotice role="alert"><p>{controller.loadError}</p></ErrorNotice>}

                        {formData && (phase === "editing" || phase === "readOnly") &&
                        (
                            <React.Fragment>
                                {phase === "readOnly" &&
                                (
                                    <InfoBanner role="note">
                                        <FontIcon iconName="Lock" />
                                        <span>{CompanyOnboardingStrings.detailsReadOnly}</span>
                                    </InfoBanner>
                                )}
                                {controller.errors.length > 0 &&
                                (
                                    <ErrorNotice role="alert">
                                        {controller.errors.map((message, index) => <p key={index}>{message}</p>)}
                                    </ErrorNotice>
                                )}

                                <DetailsLayout>
                                    <CompanyDetailsSectionNav />
                                    <div>
                                        <FormFieldset disabled={!controller.isEditable}>
                                            <CompanySectionCard id={sectionId(1)} number={1} title={CompanyOnboardingStrings.basicInfoTitle} description={CompanyOnboardingStrings.basicInfoDescription}>
                                                <FieldGrid>
                                                    <Field>
                                                        {required("Razón social")}
                                                        {textInput("businessName", 200)}
                                                        {missing("businessName")}
                                                    </Field>
                                                    <Field>
                                                        {required("Nº de identificación fiscal")}
                                                        <Input type="text" value={formData.taxId} readOnly={true} disabled={true} />
                                                    </Field>
                                                    <Field>
                                                        {required("Sector")}
                                                        <Select name="sector" value={formData.sector} onChange={controller.handleChange}>{renderOptions(SectorOptions)}</Select>
                                                        {missing("sector")}
                                                    </Field>
                                                    <Field as="div">
                                                        {required("Actividad económica (CNAE)")}
                                                        <SearchableSelectWrapper $invalid={controller.isMissing("cnae")} $disabled={!controller.isEditable}>
                                                            <DropdownSelect<CnaeOption>
                                                                options={controller.cnaeOptions}
                                                                values={controller.cnaeOptions.filter((option) => option.value === formData.cnae)}
                                                                onChange={(selected) => controller.handleCnaeChange(selected[0]?.value ?? "")}
                                                                placeholder={controller.cnaeLoading ? "Cargando…" : CompanyOnboardingStrings.cnaeSearchPlaceholder}
                                                                searchable
                                                                clearable={false}
                                                                dropdownPosition="bottom"
                                                                dropdownHeight="260px"
                                                                labelField="label"
                                                                valueField="value"
                                                                searchBy="label"
                                                                disabled={!controller.isEditable || controller.cnaeLoading}
                                                                name="cnae"
                                                                noDataLabel="No hay resultados"
                                                            />
                                                        </SearchableSelectWrapper>
                                                        {controller.cnaeError && <FieldError role="alert">{controller.cnaeError}</FieldError>}
                                                        {missing("cnae")}
                                                    </Field>
                                                    <Field>
                                                        {required("Fecha de constitución")}
                                                        {textInput("incorporationDate", 10, "date")}
                                                        {missing("incorporationDate")}
                                                    </Field>
                                                    <Field>
                                                        {required("País de constitución")}
                                                        <Select name="incorporationCountry" value={formData.incorporationCountry} onChange={controller.handleChange}>{renderOptions(CountryOptions)}</Select>
                                                        {missing("incorporationCountry")}
                                                    </Field>
                                                    <Field>
                                                        <span>Lugar de constitución</span>
                                                        {textInput("incorporationPlace", 200)}
                                                    </Field>
                                                    <Field>
                                                        {required("Nacionalidad de la empresa")}
                                                        <Select name="nationalCountry" value={formData.nationalCountry} onChange={controller.handleChange}>{renderOptions(CountryOptions)}</Select>
                                                        {missing("nationalCountry")}
                                                    </Field>
                                                    <Field>
                                                        <span>Nº de empleados</span>
                                                        {textInput("numberOfEmployees", 50)}
                                                    </Field>
                                                    <Field>
                                                        <span>Código LEI</span>
                                                        {textInput("lei", 20)}
                                                    </Field>
                                                    <Field>
                                                        <span>Fecha de expiración LEI</span>
                                                        {textInput("leiExpirationDate", 10, "date")}
                                                    </Field>
                                                    <Field>
                                                        <span>Número CNMV</span>
                                                        {textInput("numCnmv", 50)}
                                                    </Field>
                                                    <Field>
                                                        <span>Referencia SICAV</span>
                                                        {textInput("sicav", 50)}
                                                    </Field>
                                                </FieldGrid>
                                                <YesNoRow role="radiogroup" aria-label="¿La empresa dispone de firma digital?">
                                                    <span>¿La empresa dispone de firma digital?</span>
                                                    <div>
                                                        <Pill $checked={formData.digitalSignature === "yes"}>
                                                            <input type="radio" name="digitalSignature" value="yes" checked={formData.digitalSignature === "yes"} onChange={controller.handleChange} /> Sí
                                                        </Pill>
                                                        <Pill $checked={formData.digitalSignature === "no"}>
                                                            <input type="radio" name="digitalSignature" value="no" checked={formData.digitalSignature === "no"} onChange={controller.handleChange} /> No
                                                        </Pill>
                                                    </div>
                                                </YesNoRow>
                                            </CompanySectionCard>

                                            <CompanySectionCard id={sectionId(2)} number={2} title={CompanyOnboardingStrings.registryTitle} description={CompanyOnboardingStrings.registryDescription}>
                                                <FieldGrid>
                                                    <Field>
                                                        {required("Registro mercantil")}
                                                        {textInput("commercialRegistry", 200, "text", "Ej. Registro Mercantil de Madrid")}
                                                        {missing("commercialRegistry")}
                                                    </Field>
                                                    <Field>
                                                        {required("Provincia")}
                                                        <Select name="registryProvince" value={formData.registryProvince} onChange={controller.handleChange}>{renderOptions(ProvinceOptions)}</Select>
                                                        {missing("registryProvince")}
                                                    </Field>
                                                    <Field>
                                                        <span>Tomo</span>
                                                        {textInput("volume", 9, "number")}
                                                    </Field>
                                                    <Field>
                                                        <span>Folio</span>
                                                        {textInput("page", 9, "number")}
                                                    </Field>
                                                    <Field>
                                                        <span>Hoja</span>
                                                        {textInput("sheet", 50, "text", "Ej. M-123456")}
                                                    </Field>
                                                    <Field>
                                                        <span>Protocolo</span>
                                                        {textInput("protocol", 50)}
                                                    </Field>
                                                    <Field>
                                                        <span>Nombre del notario</span>
                                                        {textInput("notary", 200)}
                                                    </Field>
                                                </FieldGrid>
                                            </CompanySectionCard>

                                            <CompanySectionCard id={sectionId(3)} number={3} title={CompanyOnboardingStrings.addressesTitle} description={CompanyOnboardingStrings.addressesDescription}>
                                                <SubsectionTitle>{CompanyOnboardingStrings.fiscalAddress}</SubsectionTitle>
                                                <CompanyAddressFields address={formData.fiscalAddress} countryOptions={CountryOptions}
                                                    onChange={(field, value) => controller.handleAddressChange("fiscalAddress", field, value)}
                                                />
                                                <SubsectionTitle>{CompanyOnboardingStrings.registeredAddress}</SubsectionTitle>
                                                <CheckRow>
                                                    <input type="checkbox" checked={formData.registeredAddressSameAsFiscal} onChange={(e) => controller.handleRegisteredSameChange(e.target.checked)} />
                                                    {CompanyOnboardingStrings.registeredAddressSameAsFiscal}
                                                </CheckRow>
                                                {!formData.registeredAddressSameAsFiscal &&
                                                (
                                                    <CompanyAddressFields address={formData.registeredAddress} countryOptions={CountryOptions}
                                                        onChange={(field, value) => controller.handleAddressChange("registeredAddress", field, value)}
                                                    />
                                                )}
                                            </CompanySectionCard>

                                            <CompanySectionCard id={sectionId(4)} number={4} title={CompanyOnboardingStrings.contactTitle} description={CompanyOnboardingStrings.contactDescription}>
                                                <Field>
                                                    {required("Correo electrónico")}
                                                    {textInput("email", 255, "email", "administracion@empresa.com")}
                                                    {missing("email")}
                                                </Field>
                                                <TwoFieldRow>
                                                    <Field>
                                                        {required("Prefijo")}
                                                        <Select name="countryPrefix" value={formData.countryPrefix} onChange={controller.handleChange}>{renderOptions(PhonePrefixOptions)}</Select>
                                                        {missing("countryPrefix")}
                                                    </Field>
                                                    <Field>
                                                        {required("Número de teléfono")}
                                                        {textInput("phone", 15, "tel", "600 000 000")}
                                                        {missing("phone")}
                                                    </Field>
                                                </TwoFieldRow>
                                            </CompanySectionCard>

                                            <CompanyDocumentsSection id={sectionId(5)} number={5} documents={controller.documents} uploadingCategory={controller.uploadingCategory}
                                                disabled={!controller.isEditable} onUpload={controller.handleUploadDocuments} onDelete={controller.handleDeleteDocument}
                                            />

                                            <CompanyRepresentativesSection id={sectionId(6)} number={6} rows={formData.representatives} available={controller.availableRepresentatives}
                                                hasSelectable={controller.hasSelectableRepresentatives} borrowedIds={controller.borrowedIds} disabled={!controller.isEditable}
                                                onAdd={controller.handleAddRepresentative} onRemove={controller.handleRemoveRepresentative} onChange={controller.handleRepresentativeChange}
                                            />
                                        </FormFieldset>

                                        {controller.messages.length > 0 && <SubmitNotice role="status">{controller.messages.join(" ")}</SubmitNotice>}

                                        {controller.isEditable &&
                                        (
                                            <DetailsFooter>
                                                <CheckRow>
                                                    <input type="checkbox" checked={controller.declarationAccepted} onChange={(e) => controller.setDeclarationAccepted(e.target.checked)} />
                                                    {CompanyOnboardingStrings.declaration}
                                                </CheckRow>
                                                <DefaultButton className="button tiny-secondary-button" onClick={controller.goToIdentities} disabled={controller.isSaving}>
                                                    {CompanyOnboardingStrings.cancel}
                                                </DefaultButton>
                                                <DefaultButton className="button tiny-secondary-button" onClick={controller.handleSaveDraft} disabled={controller.isSaving || controller.uploadingCategory !== null}>
                                                    {controller.isSaving ? CompanyOnboardingStrings.savingDraft : CompanyOnboardingStrings.saveDraft}
                                                </DefaultButton>
                                                <DefaultButton className="button tiny-primary-button" onClick={controller.handleContinue} disabled={!controller.canContinue}>
                                                    {controller.isSaving && <Spinner size={SpinnerSize.small} style={{marginRight: "8px"}} />}
                                                    {CompanyOnboardingStrings.continueToKyc}
                                                </DefaultButton>
                                            </DetailsFooter>
                                        )}
                                    </div>
                                </DetailsLayout>
                            </React.Fragment>
                        )}
                    </MainCont>
                </ThemeProvider>
            </Cont>
        </DocumentTitle>
    );
};

export default CompanyDetailsScreen;
